/**
 * Check if the document is ready and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export const onDocumentReady = (callback) => {
  if (["interactive", "complete"].includes(document.readyState)) {
    callback();
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
};

/**
 * Polyfill: Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  "requestIdleCallback" in window ? window.requestIdleCallback : setTimeout;

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export const preloadImage = (src) => {
  const image = new Image();
  image.src = src;
};

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();
    // If the time since the last call exceeds the delay, execute the callback
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true },
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
// Responsive breakpoints adhere to the Tailwind CSS standards
export const mediaQueryLarge = matchMedia("(width >= 48rem)");

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Change the meta theme color of the header.
 * @param {Element} colorSourceElement - The HTML element whose background-color will determine the new theme-color.
 */
export function changeMetaThemeColor(colorSourceElement) {
  const metaThemeColor = document.head.querySelector(
    'meta[name="theme-color"]',
  );
  const containerStyle = window.getComputedStyle(colorSourceElement);
  if (metaThemeColor)
    metaThemeColor.setAttribute("content", containerStyle.backgroundColor);
}

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = "json", config = {}) {
  /** @type {Headers} */
  const headers = {
    "Content-Type": "application/json",
    Accept: `application/${type}`,
    ...config.headers,
  };

  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }

  return {
    method: "POST",
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } =
      element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(
        0,
        intersectionRight - intersectionLeft,
      );

      if (axis === "x") {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(
        0,
        intersectionBottom - intersectionTop,
      );

      if (axis === "y") {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === "x") {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === "y") {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(" ", "");
  if (valueWithNoSpaces.indexOf(",") === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(",") < valueWithNoSpaces.indexOf("."))
    return valueWithNoSpaces.replace(",", "");
  if (valueWithNoSpaces.indexOf(".") < valueWithNoSpaces.indexOf(","))
    return valueWithNoSpaces.replace(".", "").replace(",", ".");
  if (valueWithNoSpaces.indexOf(",") !== -1)
    return valueWithNoSpaces.replace(",", ".");

  return valueWithNoSpaces;
}

//  A map that keeps track of which dynamic scripts and styles have been loaded.
const dynamicLoadOnceMap = {
  script: {},
  stylesheet: {},
  scriptOnLoadEvent: {},
};

/**
 * Dynamically loads a JavaScript file by appending a `script` element to the document's `<head>`.
 * Ensures that the same script is not loaded more than once based on the source URL.
 *
 * @function
 * @param {string} src - The URL of the JavaScript file to be loaded.
 * @param {Object} [attrs={ defer: true }] - Optional attributes to be added to the `script` element (e.g., `defer`, `async`).
 *
 * @example
 * // Load a script with default defer attribute
 * window.cpap.dynamicallyLoadScript('https://example.com/script.js');
 *
 * @example
 * // Load a script with custom attributes
 * window.cpap.dynamicallyLoadScript('https://example.com/script.js', { async: true, defer: false });
 *
 * @description
 * This function dynamically creates a `script` tag and appends it to the `<head>` of the document to load an external JavaScript file.
 * If the script corresponding to the provided `src` URL has already been loaded, it will not attempt to load it again,
 * preventing duplicate script loading.
 *
 * The `attrs` parameter allows for passing in optional attributes such as `async` or `defer`. By default, the `defer` attribute
 * is set to `true` unless otherwise specified.
 *
 * A map (`dynamicLoadOnceMap.script`) is used to track previously loaded scripts to ensure they are loaded only once.
 *
 * @param {string} dynamicLoadOnceMap.script[src] - A map that keeps track of which scripts have been loaded.
 */
export const dynamicallyLoadScript = (src, attrs = { defer: true }, dataset = null) => {
  const customEventKey = `${src}__Loaded`;

  if (dynamicLoadOnceMap.script[src]) {
    if (attrs?.onload && attrs.onload instanceof Function) {
      const callOnloadEvent = () => {
        attrs.onload(dynamicLoadOnceMap.scriptOnLoadEvent[src]);
      };

      if (dynamicLoadOnceMap.scriptOnLoadEvent[src]) {
        setTimeout(callOnloadEvent);
      } else {
        // If the original load event hasn't been defined, then wait for the first load event to complete
        document.addEventListener(customEventKey, callOnloadEvent, {
          once: true,
        });
      }
    }

    return;
  }

  dynamicLoadOnceMap.script[src] = true;

  const scriptNode = document.createElement("script");
  scriptNode.src = src;

  if (!attrs?.type && !attrs?.async) {
    attrs.type = "module";
  }

  const originalOnloadCallback =
    attrs?.onload && attrs.onload instanceof Function ? attrs.onload : null;
  const scriptLoadedEvent = new CustomEvent(customEventKey);

  attrs.onload = function (event) {
    if (originalOnloadCallback) {
      originalOnloadCallback.apply(this, event);
    }
    dynamicLoadOnceMap.scriptOnLoadEvent[src] = event;
    document.dispatchEvent(scriptLoadedEvent);
  };

  Object.entries(attrs).forEach(([attr, value]) => (scriptNode[attr] = value));

  if (dataset) {
    Object.entries(dataset).forEach(([key, value]) => (scriptNode.dataset[key] = value));
  }

  document.head.appendChild(scriptNode);
};

/**
 * Dynamically loads a CSS stylesheet by creating a `<link>` element, with optional deferred loading.
 * Ensures that the stylesheet is not loaded more than once by tracking loaded URLs.
 *
 * @function
 * @param {string} href - The URL of the CSS stylesheet to load.
 * @param {boolean} [defer=true] - If `true`, defers stylesheet loading by initially setting the `media` attribute to "print" and switching it to "all" on load.
 * @param {Object} [attrs={}] - Additional attributes to set on the `<link>` element, such as `integrity` or `crossorigin`.
 *
 * @example
 * // Load a stylesheet with deferred loading
 * window.cpap.dynamicallyLoadStylesheet('https://example.com/style.css', true);
 *
 * @example
 * // Load a stylesheet without deferring and with additional attributes
 * window.cpap.dynamicallyLoadStylesheet('https://example.com/style.css', false, { integrity: 'sha384-...', crossorigin: 'anonymous' });
 *
 * @description
 * This function creates a `<link>` element for a CSS stylesheet and appends it to the `<head>` of the document.
 * If the `href` is already loaded, it skips re-loading to prevent duplicates. If `defer` is `true`, it sets the `media`
 * attribute to "print" initially to delay loading, then switches `media` to "all" when the stylesheet finishes loading.
 *
 * The `attrs` parameter allows passing additional attributes (e.g., `crossorigin`, `integrity`) to the `<link>` element.
 *
 * @property {Object} dynamicLoadOnceMap.stylesheet - A tracking object that ensures each stylesheet is loaded only once.
 */
export const dynamicallyLoadStylesheet = (href, defer = true, attrs = {}) => {
  if (dynamicLoadOnceMap.stylesheet[href]) {
    return;
  }

  dynamicLoadOnceMap.stylesheet[href] = true;

  const linkNode = document.createElement("link");
  linkNode.rel = "stylesheet";
  linkNode.href = href;

  Object.entries(attrs).forEach(([attr, value]) => (linkNode[attr] = value));

  if (defer) {
    const originalOnloadCallback =
      attrs?.onload && attrs.onload instanceof Function ? attrs.onload : null;

    // Set media to "print" initially to defer loading
    linkNode.media = "print";

    // # Change media to "all" when the stylesheet finishes loading
    linkNode.onload = (event) => {
      if (originalOnloadCallback) {
        originalOnloadCallback.apply(this, event);
      }

      linkNode.media = "all";
    };
  }

  document.head.appendChild(linkNode);
};

/**
 * Waits for the Gorgias Chat widget to be ready.
 *
 * - If `window.GorgiasChat` is already available, it initializes the chat immediately.
 * - Otherwise, it waits for the `gorgias-widget-loaded` event and resolves once fired.
 *
 * @async
 * @function onGorgiasReady
 * @returns {Promise<void>} A promise that resolves when the Gorgias Chat widget is initialized and ready.
 */
export const onGorgiasReady = async () => {
  return window.GorgiasChat
    ? window.GorgiasChat.init()
    : new Promise((resolve) =>
        window.addEventListener("gorgias-widget-loaded", resolve, {
          once: true,
        }),
      );
};
